import Grid from '@mui/material/Grid';
import { Dispatch, memo, SetStateAction, useCallback, useMemo, useState } from 'react';

import { IcaoIataCode } from '../../../../../../../../backend';
import { SquareButton } from '../../../../../../../Button/SquareButton/SquareButton';
import { AirlinesList } from './AirlinesList';
import { intersection, not } from './TransfertList.utils';

export const TransfertList = memo(function TransfertList({
    airlines,
    search,
    selection,
    setSelection,
}: {
    airlines: IcaoIataCode[] | undefined;
    search: string;
    selection: number[];
    setSelection: Dispatch<SetStateAction<number[]>>;
}) {
    const [checked, setChecked] = useState<number[]>([]);
    const [left, setLeft] = useState<number[]>(
        airlines.map(v => airlines.indexOf(v)).filter(v => !selection.includes(v)),
    );

    const leftChecked = useMemo(() => intersection(checked, left), [checked, left]);
    const rightChecked = useMemo(() => intersection(checked, selection), [checked, selection]);

    const handleAllRight = useCallback(() => {
        setSelection(selection.concat(left));
        setLeft([]);
    }, [left, selection, setSelection]);

    const handleCheckedRight = useCallback(() => {
        setSelection(selection.concat(leftChecked));
        setLeft(not(left, leftChecked));
        setChecked(o => not(o, leftChecked));
    }, [left, leftChecked, selection, setSelection]);

    const handleCheckedLeft = useCallback(() => {
        setLeft(left.concat(rightChecked));
        setSelection(not(selection, rightChecked));
        setChecked(o => not(o, rightChecked));
    }, [left, rightChecked, selection, setSelection]);

    const handleAllLeft = useCallback(() => {
        setLeft(left.concat(selection));
        setSelection([]);
    }, [left, selection, setSelection]);

    return (
        <Grid container spacing={2} justifyContent="center" alignItems="center">
            <Grid item>
                <AirlinesList
                    search={search}
                    items={left}
                    airlines={airlines}
                    setChecked={setChecked}
                    checked={checked}
                />
            </Grid>
            <Grid item>
                <Grid container direction="column" alignItems="center">
                    <SquareButton sx={{ my: 0.5 }} size="small" onClick={handleAllRight} disabled={left.length === 0}>
                        ≫
                    </SquareButton>
                    <SquareButton
                        sx={{ my: 0.5 }}
                        size="small"
                        onClick={handleCheckedRight}
                        disabled={leftChecked.length === 0}
                    >
                        &gt;
                    </SquareButton>
                    <SquareButton
                        sx={{ my: 0.5 }}
                        size="small"
                        onClick={handleCheckedLeft}
                        disabled={rightChecked.length === 0}
                    >
                        &lt;
                    </SquareButton>
                    <SquareButton
                        sx={{ my: 0.5 }}
                        size="small"
                        onClick={handleAllLeft}
                        disabled={selection.length === 0}
                    >
                        ≪
                    </SquareButton>
                </Grid>
            </Grid>
            <Grid item>
                <AirlinesList
                    search={search}
                    items={selection}
                    airlines={airlines}
                    setChecked={setChecked}
                    checked={checked}
                />
            </Grid>
        </Grid>
    );
});
